// Real BCM photography only.
//
// TWO SOURCES
// • "2026–27 BCM Photos" — the current-year album (Summer ’26, Fall ’26, Spring ’27).
// • "Public BCM Photos" — verified historical photography from Fall 2025 and
//   Spring 2026, used as a temporary visual source until new Fall 2026
//   photography exists.
//
// HOW TO MAINTAIN THIS FILE
// Paste each photo's hosted URL into `src`. Until then the frame renders a
// neutral "Add Image here" placeholder — never stock or AI-generated people.
// Historical photos must never be presented as upcoming events, current
// leadership, or a current schedule. Only add a `caption` when the source
// folder clearly identifies the event.

export type AlbumName =
  | 'Summer ’26'
  | 'Fall ’26'
  | 'Spring ’27'
  | 'Fall ’25'
  | 'Spring ’26'

export type BcmPhoto = {
  id: string
  album: AlbumName
  /** Verified subject/event from the source folder. */
  subject: string
  /** Meaningful, general alt text for the real image. */
  alt: string
  /** Only set when the source folder clearly identifies the event. */
  caption?: string
  /** Hosted URL of the real BCM photograph. Leave empty until supplied. */
  src?: string
}

export const albumMeta: {
  name: AlbumName
  hasPhotos: boolean
  status: string
  /** Public-facing message shown when the folder has no photos yet. */
  message?: string
  note: string
}[] = [
  {
    name: 'Summer ’26',
    hasPhotos: true,
    status: 'Photos available',
    note: 'Pickleball Night and Tabling photography.',
  },
  {
    name: 'Fall ’26',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Fall photos will be added after the first worship night on August 27.',
    note: 'Folder created — no photos yet.',
  },
  {
    name: 'Spring ’27',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Spring photos will be added as the semester begins.',
    note: 'Folder created — no photos yet.',
  },
]

/** Current-year album: 2026–27 BCM Photos. */
export const currentYearPhotos: BcmPhoto[] = [
  {
    id: 'summer26-pickleball-1',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'UCF BCM students playing together at a pickleball night.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-1',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'UCF BCM students tabling on campus and meeting new students.',
    caption: 'Tabling on campus · Summer ’26',
  },
  {
    id: 'summer26-pickleball-2',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'UCF BCM students talking and laughing between games.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-2',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'A UCF BCM student sharing information about Thursday worship nights.',
    caption: 'Tabling on campus · Summer ’26',
  },
]

/**
 * Historical album: Public BCM Photos (Fall 2025 + Spring 2026).
 * Temporary visual source — never labeled as an upcoming event or as the
 * current leadership team.
 */
export const pastPhotos: BcmPhoto[] = [
  {
    id: 'past-worship-1',
    album: 'Fall ’25',
    subject: 'Worship night',
    alt: 'UCF BCM students gathering at a worship night.',
    caption: 'Worship night · Fall ’25',
  },
  {
    id: 'past-community-group-1',
    album: 'Fall ’25',
    subject: 'Community group',
    alt: 'UCF BCM students sitting together during a community group.',
    caption: 'Community group · Fall ’25',
  },
  {
    id: 'past-volleyball-1',
    album: 'Fall ’25',
    subject: 'Volleyball night',
    alt: 'UCF BCM students playing volleyball together.',
    caption: 'Volleyball night · Fall ’25',
  },
  {
    id: 'past-outreach-1',
    album: 'Fall ’25',
    subject: 'Campus outreach',
    alt: 'UCF BCM students meeting other students during campus outreach.',
    caption: 'Campus outreach · Fall ’25',
  },
  {
    id: 'past-friendsgiving-1',
    album: 'Fall ’25',
    subject: 'Friendsgiving',
    alt: 'UCF BCM students sharing a meal together at Friendsgiving.',
    caption: 'Friendsgiving · Fall ’25',
  },
  {
    id: 'past-camping-1',
    album: 'Fall ’25',
    subject: 'Camping trip',
    alt: 'UCF BCM students on a camping trip together.',
    caption: 'Camping trip · Fall ’25',
  },
  {
    id: 'past-formal-1',
    album: 'Spring ’26',
    subject: 'Formal',
    alt: 'UCF BCM students dressed up together at a formal event.',
    caption: 'Formal · Spring ’26',
  },
  {
    id: 'past-collegiate-weekend-1',
    album: 'Spring ’26',
    subject: 'Collegiate Weekend',
    alt: 'UCF BCM students together during Collegiate Weekend.',
    caption: 'Collegiate Weekend · Spring ’26',
  },
  {
    id: 'past-worship-2',
    album: 'Spring ’26',
    subject: 'Worship night',
    alt: 'UCF BCM students singing together during a worship night.',
    caption: 'Worship night · Spring ’26',
  },
  {
    id: 'past-community-group-2',
    album: 'Spring ’26',
    subject: 'Community group',
    alt: 'UCF BCM students talking and praying together in a community group.',
    caption: 'Community group · Spring ’26',
  },
]

/**
 * Supplied directly by BCM leadership rather than from a Drive album. No
 * caption: the specific event is not verified, so the photo is used as a
 * general group image only.
 */
export const suppliedPhotos: BcmPhoto[] = [
  {
    id: 'supplied-group-1',
    album: 'Spring ’26',
    subject: 'Group photo',
    alt: 'A group of UCF BCM students standing together and smiling.',
    src: 'https://cdn.magicpatterns.com/uploads/wGzJRkdNzqHrUdkwaWTCYX/IMG_6357.jpg',
  },
  {
    id: 'supplied-worship-band-1',
    album: 'Spring ’26',
    subject: 'Worship night',
    alt: 'UCF BCM students leading worship with microphones and guitars on a lit stage.',
    src: 'https://cdn.magicpatterns.com/uploads/7ZTyrpsQzA1zjLGWkYmG6g/IMG_2146.jpg',
  },
  {
    id: 'supplied-gathering-1',
    album: 'Spring ’26',
    subject: 'Worship night',
    alt: 'UCF BCM students seated together facing the stage during a worship night, with the UCF BCM logo on the screen above.',
    src: 'https://cdn.magicpatterns.com/uploads/tAnFudxMEAN5LgwcfFZLAk/Typical_week.jpg',
  },
  {
    id: 'supplied-banner-1',
    album: 'Spring ’26',
    subject: 'Worship night banner',
    alt: 'A UCF BCM banner outside Cape Florida Ballroom reading “Worship Night – 7:30PM, Bring a Friend!”',
    src: 'https://cdn.magicpatterns.com/uploads/nMFnhN1VibZCesETS6EVyg/BCM.jpg',
  },
  {
    id: 'supplied-circle-worship-1',
    album: 'Spring ’26',
    subject: 'Students gathered together',
    alt: 'UCF BCM students sitting in a circle singing together while a student leads on guitar.',
    src: 'https://cdn.magicpatterns.com/uploads/gWatJevcJ6yfgzUBWqZhAh/image.png',
  },
  {
    id: 'supplied-tables-1',
    album: 'Spring ’26',
    subject: 'Students gathered together',
    alt: 'UCF BCM students sitting around round tables talking and laughing together.',
    src: 'https://cdn.magicpatterns.com/uploads/gJYY4h5LCBihC2Go8c7bmz/49894176_Unknown.jpg',
  },
]

const byId = (id: string): BcmPhoto => {
  const all = [...currentYearPhotos, ...pastPhotos, ...suppliedPhotos]
  const found = all.find((photo) => photo.id === id)
  if (!found) throw new Error(`Unknown photo id: ${id}`)
  return found
}

/** Hero collage: friendship, recreation, and outreach — a deliberate mix. */
export const heroPhotos: BcmPhoto[] = [
  byId('supplied-circle-worship-1'),
  byId('supplied-group-1'),
  byId('supplied-worship-band-1'),
]

/** "Come as you are" pair: community group + shared meal. */
export const galleryPhotos: BcmPhoto[] = [
  byId('past-community-group-1'),
  byId('supplied-tables-1'),
  byId('supplied-banner-1'),
]

/** Typical Week section photo (landscape crop). */
export const typicalWeekPhoto: BcmPhoto = byId('supplied-gathering-1')

/** Temporary poster image for the welcome video component. */
export const videoPosterPhoto: BcmPhoto = byId('past-worship-2')
